<template>
  <div class="tw:py-6">
    <p class="tw:text-sm">
      {{ $t('hotel_offers_warning_message') }}
    </p>
    <div class="tw:py-6">
      <div class="tw:mb-4">
        <p class="tw:font-bold tw:text-gray-800">{{ $t('hotel_offers_posted') }}</p>
        <p class="tw:text-xs tw:text-gray-600">{{ $t('hotel_offers_change_info') }}</p>
      </div>

      <div v-if="!isLoading" class="tw:border tw:border-gray-300 tw:rounded-xl tw:bg-gray-100">
        <div class="tw:flex tw:flex-row tw:items-center tw:p-4">
          <div class="tw:flex tw:items-center tw:justify-start tw:gap-x-3 tw:w-full">
            <img
              class="tw:h-[36px] tw:w-[36px] tw:grayscale"
              :src="hotelOfferInactiveIcon"
              :alt="$t(`ho_${activeOffer.template}_title`)"
            />
            <div class="tw:flex tw:flex-col tw:justify-start tw:items-start tw:grow">
              <p
                v-if="currentActiveOffer.type === 'DISCOUNT' && activeOffer.benefitValue > 0"
                class="tw:font-bold tw:text-gray-800"
              >
                {{ translationObject[`ho_${activeOffer.template}_discount_title`] }}
              </p>
              <p
                v-if="currentActiveOffer.type === 'DISCOUNT' && activeOffer.benefitValue === null"
                class="tw:font-bold tw:text-gray-800"
              >
                {{ translationObject[`ho_${activeOffer.template}_free_title`] }}
              </p>
              <p v-if="currentActiveOffer.type === 'FREE'" class="tw:text-left tw:font-bold">
                {{ translationObject[`ho_${activeOffer.template}_title`] }}
              </p>

              <p class="tw:text-left tw:text-sm">
                {{ translationObject[`ho_${activeOffer.template}_text`] }}
              </p>
              <p v-if="hasCancellationDate" class="tw:text-xs tw:text-gray-600">
                {{ $t('hotel_offers_validity_date') }}<span class="tw:ml-1">{{ validityDate }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tw:animate-pulse tw:w-full">
        <div class="tw:rounded-lg tw:bg-gray-300 tw:w-full tw:h-[78px]" />
      </div>
    </div>
    <div>
      <div class="tw:mb-4">
        <p class="tw:font-bold tw:text-gray-800">{{ $t('hotel_offers_new') }}</p>
        <p class="tw:text-xs tw:text-gray-600">{{ $t('hotel_offers_new_info') }}</p>
      </div>

      <div v-if="!isLoading" class="tw:border tw:border-gray-300 tw:rounded-xl tw:bg-blue-100">
        <div class="tw:flex tw:flex-row tw:items-center tw:p-4">
          <div class="tw:flex tw:items-center tw:justify-start tw:gap-x-3 tw:w-full">
            <img class="tw:h-[36px] tw:w-[36px]" :src="hotelOfferIcon" :alt="$t(newOffer.name)" />
            <div class="tw:flex tw:flex-col tw:justify-start tw:items-start tw:grow">
              <p v-if="newOffer.type === 'DISCOUNT' && discountValue > 0" class="tw:font-bold tw:text-gray-800">
                {{ translationObject[`ho_${newOffer.name}_discount_title`] }}
              </p>
              <p v-if="newOffer.type === 'DISCOUNT' && discountValue === null" class="tw:font-bold tw:text-gray-800">
                {{ translationObject[`ho_${newOffer.name}_free_title`] }}
              </p>
              <p v-if="newOffer.type === 'FREE'" class="tw:text-left tw:font-bold">
                {{ translationObject[`ho_${newOffer.name}_title`] }}
              </p>
              <p class="tw:text-left tw:text-sm">
                {{ translationObject[`ho_${newOffer.name}_text`] }}
              </p>
              <p v-if="selectedDateOption === 1" class="tw:text-xs tw:text-gray-600">
                {{ $t('hotel_offers_validity_date') }}<span class="tw:ml-1">{{ newOfferValidityDate }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tw:animate-pulse tw:w-full">
        <div class="tw:rounded-lg tw:bg-gray-300 tw:w-full tw:h-[78px]" />
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useOffersTemplates, useOfferTranslations } from '@/components/hotelOffers/queries'
import { getSnippetsForQuery } from '@/utils/hotelOffersUtils'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const hotelOfferIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/offer.png`, import.meta.url)
const hotelOfferInactiveIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/offer-inactive.png`, import.meta.url)

const props = defineProps({
  newOffer: Object,
  activeOffer: Object,
  hasCancellationDate: Boolean,
  selectedOfferType: Number,
  discountValue: Number,
  selectedDateOption: Number,
  expirationDate: [Date, null],
})

const { formatDate, formatPercentage } = useFormatting()
const { language } = useSessionValues()
const { templates } = useOffersTemplates()

const newOfferDiscountedNumberFormatted = computed(() => formatPercentage(props.discountValue, 0, 0))
const currentOfferDiscountedNumberFormatted = computed(() =>
  formatPercentage(props.activeOffer?.benefitValue ?? 0, 0, 0)
)

const validityDate = computed(() =>
  props.activeOffer.expirationDate
    ? formatDate(new Date(props.activeOffer.expirationDate.replace('@', 'T')), {
        timeZone: 'UTC',
      })
    : ''
)

const newOfferValidityDate = computed(() =>
  formatDate(props.expirationDate, {
    timeZone: 'UTC',
  })
)

const currentActiveOffer = computed(() => templates.value.find(item => item.name === props.activeOffer.template))
const { isLoading, data } = useOfferTranslations({
  snippets: getSnippetsForQuery([
    {
      offer: props.newOffer,
      discountValue: props.discountValue,
      formattedDiscount: newOfferDiscountedNumberFormatted.value,
    },
    {
      offer: currentActiveOffer.value,
      discountValue: props.activeOffer.discountValue,
      formattedDiscount: currentOfferDiscountedNumberFormatted.value,
    },
  ]),
  languageCode: language.value,
})

const translationObject = computed(() =>
  (data.value ?? []).reduce((acc, item) => {
    acc[item.snippet] = item.translation
    return acc
  }, {})
)
</script>
